import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  fetchIseRuleConditionFields,
  extractRuleConditionFieldOptions,
} from '@/services/fieldMappingService'
import {
  DEFAULT_RULE_CONDITION_FIELD_OPTIONS,
  type RuleConditionFieldOption,
} from '@/utils/ruleConditionFields'

/**
 * Canonical client-side source of truth for Publish Rule / Vehicle Grouping
 * condition fields.
 *
 * Loads the deliberate, server-owned catalog (GET /mapping-config/rule-fields)
 * once and caches it. Every rule-condition surface (editors and read-only rule
 * summaries) reads field availability and labels from here, so behavior and
 * labels stay consistent and account-independent — never inferred from an
 * account's saved rules.
 */
export const useRuleConditionFieldsStore = defineStore('ruleConditionFields', () => {
  const options = ref<RuleConditionFieldOption[]>([])
  const loaded = ref(false)
  const loading = ref(false)

  async function load(force = false): Promise<void> {
    if ((loaded.value || loading.value) && !force) return
    loading.value = true
    try {
      const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
      const resp = await fetchIseRuleConditionFields(iseEndpoint)
      const opts = extractRuleConditionFieldOptions(resp)
      // Fail soft to defaults only when the API is unreachable/empty.
      options.value = opts.length > 0 ? opts : DEFAULT_RULE_CONDITION_FIELD_OPTIONS
    } catch {
      options.value = DEFAULT_RULE_CONDITION_FIELD_OPTIONS
    } finally {
      loaded.value = true
      loading.value = false
    }
  }

  /** User-facing label for a rule-condition field, or undefined if unknown. */
  function labelFor(key: string): string | undefined {
    return options.value.find((o) => o.value === key)?.title
  }

  /** Whether a rule-condition field is system-provided (not user-mapped). */
  function isSystemProvided(key: string): boolean {
    return options.value.find((o) => o.value === key)?.systemProvided === true
  }

  return { options, loaded, loading, load, labelFor, isSystemProvided }
})
